import { createGlobalStyle } from 'styled-components';

// Create a properly typed GlobalStyles component
export const GlobalStyles = createGlobalStyle`
  :root {
    /* Colors */
    --primary: ${({ theme }) => theme.colors.primary};
    --primary-hover: ${({ theme }) => theme.colors.primaryHover};
    --background: ${({ theme }) => theme.colors.background};
    --text: ${({ theme }) => theme.colors.text};
    --text-secondary: ${({ theme }) => theme.colors.textSecondary};
    --border: ${({ theme }) => theme.colors.border};
    --error: ${({ theme }) => theme.colors.error};
    --disabled: ${({ theme }) => theme.colors.disabled};
    --surface: ${({ theme }) => theme.colors.surface};
    --success: ${({ theme }) => (theme.colors as any).success || '#10b981'};
    --warning: ${({ theme }) => (theme.colors as any).warning || '#f59e0b'};
    --info: ${({ theme }) => (theme.colors as any).info || '#3b82f6'};
    
    /* Spacing */
    --spacing-xs: ${({ theme }) => theme.spacing.xs};
    --spacing-sm: ${({ theme }) => theme.spacing.sm};
    --spacing-md: ${({ theme }) => theme.spacing.md};
    --spacing-lg: ${({ theme }) => theme.spacing.lg};
    --spacing-xl: ${({ theme }) => theme.spacing.xl};
    
    /* Typography */
    --font-size-xs: ${({ theme }) => theme.fontSize.xs};
    --font-size-sm: ${({ theme }) => theme.fontSize.sm};
    --font-size-md: ${({ theme }) => theme.fontSize.md};
    --font-size-lg: ${({ theme }) => theme.fontSize.lg};
    --font-size-xl: ${({ theme }) => theme.fontSize.xl};
    
    --font-weight-normal: ${({ theme }) => theme.fontWeight.normal};
    --font-weight-medium: ${({ theme }) => theme.fontWeight.medium};
    --font-weight-semibold: ${({ theme }) => theme.fontWeight.semibold};
    --font-weight-bold: ${({ theme }) => theme.fontWeight.bold};
    
    /* Border Radius */
    --border-radius-sm: ${({ theme }) => theme.borderRadius.sm};
    --border-radius-md: ${({ theme }) => theme.borderRadius.md};
    --border-radius-lg: ${({ theme }) => theme.borderRadius.lg};
    --border-radius-full: ${({ theme }) => theme.borderRadius.full};
    
    /* Transitions */
    --transition-fast: 0.1s ease-in-out;
    --transition-default: 0.2s ease-in-out;
    --transition-slow: 0.3s ease-in-out;
  }
  
  /* Reset and base styles */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    font-size: 16px;
    height: 100%;
    scroll-behavior: smooth;
  }
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: var(--background);
    color: var(--text);
    line-height: 1.5;
    height: 100%;
    transition: background-color var(--transition-default), color var(--transition-default);
  }
  
  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
  
  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    margin: 0 0 1rem 0;
    font-weight: 600;
    line-height: 1.2;
    color: var(--text);
  }
  
  h1 { font-size: 2.5rem; }
  h2 { font-size: 2rem; }
  h3 { font-size: 1.75rem; }
  h4 { font-size: 1.5rem; }
  h5 { font-size: 1.25rem; }
  h6 { font-size: 1rem; }
  
  p {
    margin: 0 0 1rem 0;
    color: var(--text);
  }
  
  a {
    color: var(--primary);
    text-decoration: none;
    transition: color var(--transition-default);
    
    &:hover {
      color: var(--primary-hover);
      text-decoration: underline;
    }
  }
  
  /* Buttons */
  button {
    cursor: pointer;
    font-family: inherit;
    font-size: 1rem;
    line-height: 1.5;
    padding: 0.5rem 1rem;
    border-radius: var(--border-radius-md);
    border: 1px solid transparent;
    background-color: var(--primary);
    color: white;
    transition: all var(--transition-default);

    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
    }

    &:disabled {
      background-color: var(--disabled);
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
  
  /* Forms */
  input,
  select,
  textarea {
    font-family: inherit;
    font-size: 1rem;
    line-height: 1.5;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border);
    border-radius: var(--border-radius-md);
    background-color: var(--background);
    color: var(--text);
    transition: border-color var(--transition-default), box-shadow var(--transition-default);

    &:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.2);
    }

    &::placeholder {
      color: var(--text-secondary);

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      background-color: ${({ theme }) => theme.colors.textMuted};
      cursor: not-allowed;
      transform: none;
    }

    &:focus {
      outline: none;
    }
  }

  /* Scrollbar styles */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.borderLight};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.textMuted};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.colors.textSecondary};
    }
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.textMuted} ${({ theme }) => theme.colors.borderLight};
  }

  /* Hide scrollbar for specific elements */
  .no-scrollbar {
    -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;  /* Firefox */
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;  /* Safari and Chrome */
  }

  /* Smooth scroll behavior for chat */
  .chat-scrollbar {
    scroll-behavior: smooth;
    scroll-padding-bottom: 20px;
  }

  /* Enhanced smooth scrolling for all browsers */
  html {
    scroll-behavior: smooth;
  }

  /* Thinking dots animation */
  @keyframes thinking-dot {
    0%, 80%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 1;
      transform: scale(1.2);
    }
  }

  /* Selection */
  ::selection {
    background-color: ${({ theme }) => theme.colors.primary}40;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  /* Focus visible */
  :focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Utility classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .font-bold {
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }

  .font-semibold {
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
  }

  .font-medium {
    font-weight: ${({ theme }) => theme.fontWeight.medium};
  }

  .text-primary {
    color: ${({ theme }) => theme.colors.primary};
  }

  .text-secondary {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  .text-muted {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  .text-error {
    color: ${({ theme }) => theme.colors.error};
  }

  .text-success {
    color: ${({ theme }) => theme.colors.success};
  }

  .text-warning {
    color: ${({ theme }) => theme.colors.warning};
  }

  .text-info {
    color: ${({ theme }) => theme.colors.info};
  }

  /* Animation classes */
  .fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }

  .slide-in-right {
    animation: slideInRight 0.3s ease-out;
  }

  .slide-in-left {
    animation: slideInLeft 0.3s ease-out;
  }

  .bounce-in {
    animation: bounceIn 0.5s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideInLeft {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes bounceIn {
    0% {
      transform: scale(0.3);
      opacity: 0;
    }
    50% {
      transform: scale(1.05);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* Loading spinner */
  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Responsive design */
  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }

    h1 {
      font-size: ${({ theme }) => theme.fontSize.xl};
    }

    h2 {
      font-size: ${({ theme }) => theme.fontSize.xl};
    }

    h3 {
      font-size: ${({ theme }) => theme.fontSize.lg};
    }
  }

  @media (max-width: 480px) {
    html {
      font-size: 13px;
    }
  }

  /* Print styles */
  @media print {
    * {
      background: white !important;
      color: black !important;
      box-shadow: none !important;
    }

    a, a:visited {
      text-decoration: underline;
    }

    .no-print {
      display: none !important;
    }
  }
`;

export default GlobalStyles;
